import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const pg = (mid) => `${API}/matters/${mid}/proofgraph`;

export const proofApi = {
  summary: (mid) => axios.get(pg(mid)).then((r) => r.data),
  board: (mid) => axios.get(`${pg(mid)}/board`).then((r) => r.data),
  gaps: (mid, status) => axios.get(`${pg(mid)}/gaps`, { params: status ? { status } : {} }).then((r) => r.data),
  gapScan: (mid) => axios.post(`${pg(mid)}/gap-scan`).then((r) => r.data),
  analyse: (mid, body) => axios.post(`${pg(mid)}/analyse`, body || {}).then((r) => r.data),
  run: (mid, rid) => axios.get(`${pg(mid)}/runs/${rid}`).then((r) => r.data),
  createNode: (mid, body) => axios.post(`${pg(mid)}/nodes`, body).then((r) => r.data),
  patchNode: (mid, id, body) => axios.patch(`${pg(mid)}/nodes/${id}`, body).then((r) => r.data),
  createEdge: (mid, body) => axios.post(`${pg(mid)}/edges`, body).then((r) => r.data),
  patchEdge: (mid, id, body) => axios.patch(`${pg(mid)}/edges/${id}`, body).then((r) => r.data),
  verifyRequirement: (mid, id, body) => axios.post(`${pg(mid)}/requirements/${id}/verify`, body || {}).then((r) => r.data),
  resolveGap: (mid, id, body) => axios.post(`${pg(mid)}/gaps/${id}/resolve`, body).then((r) => r.data),
  dismissGap: (mid, id, reason) => axios.post(`${pg(mid)}/gaps/${id}/dismiss`, { reason }).then((r) => r.data),
  reopenGap: (mid, id) => axios.post(`${pg(mid)}/gaps/${id}/reopen`).then((r) => r.data),
  patchGap: (mid, id, body) => axios.patch(`${pg(mid)}/gaps/${id}`, body).then((r) => r.data),
  review: (mid, body) => axios.post(`${pg(mid)}/review`, body).then((r) => r.data),
  acceptAll: (mid) => axios.post(`${pg(mid)}/review/accept-all`).then((r) => r.data),
  audit: (mid) => axios.get(`${pg(mid)}/audit`).then((r) => r.data),
  crossExamObjectives: (mid) => axios.get(`${API}/crossexam/objectives`, { params: mid ? { matterId: mid } : {} }).then((r) => r.data),
};

export const OBJECTIVE_TYPES = ["RELIEF", "CLAIM", "DEFENCE"];
export const LAW_TYPES = ["ISSUE", "LEGAL_REQUIREMENT"];
export const FACT_TYPES = ["FACT", "PLEADING_PROPOSITION", "ADMISSION", "DENIAL"];
export const RECORD_TYPES = ["DOCUMENT", "DOCUMENT_EXTRACT", "EVIDENCE_ARTIFACT", "WITNESS", "TESTIMONY", "EXHIBIT", "ORDER", "HEARING_NOTE", "ADMISSION"];
export const DOC_TYPES = ["DOCUMENT", "DOCUMENT_EXTRACT", "EVIDENCE_ARTIFACT", "EXHIBIT"];
export const SUPPORT_REL = ["SUPPORTS", "PARTIALLY_SUPPORTS", "SOURCE_OF", "EXHIBITED_AS", "ADMITS"];
export const ATTACK_REL = ["CONTRADICTS", "UNDERMINES", "CHALLENGES", "DENIES"];
export const VERIFIED = ["ADVOCATE_VERIFIED", "SENIOR_VERIFIED", "EXTERNAL_SOURCE_VERIFIED", "DOCUMENT_VERIFIED", "SOURCE_LINKED"];
export const EXHIBIT_STATUSES = ["NOT_PROPOSED", "PROPOSED", "FILED", "MARKED", "OBJECTED", "ADMITTED_FOR_LIMITED_PURPOSE", "REJECTED", "UNKNOWN"];
export const COVERAGE = ["ADMITTED", "DOCUMENTED", "ALLEGED", "DISPUTED", "INFERRED", "MISSING", "UNKNOWN"];
export const FOUNDATION_FIELDS = [
  ["authenticityDisputed", "Authenticity disputed"], ["executionDisputed", "Execution disputed"], ["copyOnly", "Copy only (no original)"],
  ["translationRequired", "Translation required"], ["certificationRequired", "Certification required"], ["admissibilityIssue", "Admissibility issue"],
  ["electronicRecordReview", "Electronic-record foundation review"],
];

// Index nodes/edges for fast traversal.
export function indexGraph(nodes, edges) {
  const N = Object.fromEntries(nodes.map((n) => [n.id, n]));
  const out = {}; const inn = {};
  edges.forEach((e) => { if (!N[e.fromId] || !N[e.toId]) return; (out[e.fromId] ||= []).push(e); (inn[e.toId] ||= []).push(e); });
  const rel = (list, rels, types, key) => (list || []).filter((e) => rels.includes(e.relationType) && (!types || types.includes(N[e[key]]?.nodeType))).map((e) => ({ ...N[e[key]], edge: e }));
  return {
    N, out, inn,
    children: (id) => rel(out[id], ["REQUIRES", "DEPENDS_ON"], null, "toId"),
    supporters: (id) => rel(inn[id], SUPPORT_REL, RECORD_TYPES, "fromId"),
    factSupporters: (id) => rel(inn[id], SUPPORT_REL, FACT_TYPES, "fromId"),
    attackers: (id) => rel(inn[id], ATTACK_REL, null, "fromId"),
    witnesses: (id) => rel(inn[id], ["WITNESS_TO"], ["WITNESS"], "fromId"),
    authorities: (id) => rel(inn[id], ["AUTHORITY_FOR"], ["AUTHORITY"], "fromId"),
    pleadings: (id) => rel(inn[id], ["PLEADS"], null, "fromId"),
    cautions: (id) => rel(inn[id], ["CAUTIONS"], ["CONFLICT"], "fromId"),
    rebuttals: (id) => rel(inn[id], ["REBUTS", "RESPONDS_TO"], null, "fromId"),
    parents: (id) => rel(inn[id], ["REQUIRES", "DEPENDS_ON"], null, "fromId"),
  };
}

// Structural (never outcome) status for a requirement or proposition.
export function deriveStatus(node, G) {
  if (!node) return "UNKNOWN";
  if (node.status === "PROPOSED") return "NEEDS_VERIFICATION";
  if (node.nodeType === "LEGAL_REQUIREMENT") {
    const facts = G.children(node.id).filter((c) => FACT_TYPES.includes(c.nodeType));
    const direct = G.supporters(node.id);
    const supported = facts.filter((f) => G.supporters(f.id).length || f.coverage === "ADMITTED");
    const attacked = facts.some((f) => G.attackers(f.id).length) || G.attackers(node.id).length;
    if (!direct.length && !supported.length) return "UNSUPPORTED";
    if (facts.length && supported.length < facts.length) return attacked ? "CONTESTED" : "PARTIALLY_SUPPORTED";
    return attacked ? "CONTESTED" : "SUPPORTED";
  }
  if (FACT_TYPES.includes(node.nodeType)) {
    if (node.coverage === "ADMITTED") return "ADMITTED";
    if (node.nodeType === "DENIAL") return "DENIED";
    const s = G.supporters(node.id).length; const a = G.attackers(node.id).length;
    if (a && s) return "CONTESTED";
    if (a) return "CONTRADICTED";
    if (s) return "SUPPORTED";
    return "UNSUPPORTED";
  }
  if (node.nodeType === "AUTHORITY") return VERIFIED.includes(node.verificationStatus) ? "SUPPORTED" : "NEEDS_VERIFICATION";
  if (node.nodeType === "CONFLICT") return node.conflict?.resolution ? "SUPPORTED" : "CONTESTED";
  return "NOT_APPLICABLE";
}

export const humanize = (s) => (s || "").toString().replace(/_/g, " ").toLowerCase().replace(/(^|\s)\S/g, (t) => t.toUpperCase());
